import React, { useCallback, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import { useLocation, useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import { FormWrapper } from 'ksnm-common-ui/lib/Form/FormWrapper';
import { Notification } from 'ksnm-common-ui/lib/Notification';

import { DesignLayout } from '@common/DesignLayout';
import { RoutePath } from '@components/AppRouter/constants';
import { usePermission } from '@src/utils/hooks/usePermission';
import { controlEditStore } from '@stores/dictionaryStores/controlsStore/controlEditStore';
import { controlsStore } from '@stores/dictionaryStores/controlsStore/controlsStore';

import { NEW_ENTITY_ID } from '../../../AppRouter/hooks/constants';
import { Form } from './Form';
import { validate } from './validateSchema';

export const ControlEdit = observer(() => {
  const { ctrlCode } = useParams<{ ctrlCode: string }>();
  const { setNew, fetch, setEditMode, control, save, isNew, isEditMode } = controlEditStore;
  const { selectNode } = controlsStore;

  const { state } = useLocation();
  const { push } = useHistory();

  const permission = usePermission('CmControl', controlEditStore);

  useEffect(() => {
    if (ctrlCode !== NEW_ENTITY_ID) {
      fetch(ctrlCode);
    } else {
      setNew(true);
    }
    return () => {
      setNew(false);
      setEditMode(false);
    };
  }, [fetch, setNew, setEditMode, ctrlCode]);

  const initialValues = useMemo(() => {
    if (state && state.ctrlGroupCode) {
      return { ctrlGroupCode: state.ctrlGroupCode };
    }

    return control
      ? { ...control, id: control?.ctrlCode }
      : { repGroupCode: selectNode?.repGroupCode, ctrlGroupCode: selectNode?.ctrlGroupCode }; // id нужен для проверки на уникальность кода
  }, [control, selectNode?.ctrlGroupCode, selectNode?.repGroupCode, state]);

  const handleSubmit = useCallback(
    (values) => {
      if ((isNew && !permission.CREATE) || (!isNew && !permission.UPDATE)) {
        Notification({
          message: `У вас нет прав для выполнения операции ${isNew ? 'создания' : 'обновления'} формы`,
        });
        return {};
      }

      const validation = validate(values);

      if (validation) {
        return validation;
      }

      (async () => {
        const value = await save(values);

        if (isNew && value?.ctrlCode) {
          push(`${RoutePath.DICTIONARY_CONTROLS}/${value.ctrlCode}`);
        }
      })();

      return undefined;
    },
    [isNew, permission.CREATE, permission.UPDATE, push, save],
  );

  return (
    <DesignLayout title="Справочник: Контроли" scrollEnable requiredHelp={isEditMode}>
      <FormWrapper initialValues={initialValues} onSubmit={handleSubmit} layout="vertical" compact>
        <Form />
      </FormWrapper>
    </DesignLayout>
  );
});
